import {
  AgentType,
  AssistantConfig,
  HistoricChat,
  LoadedChat,
} from '@ardoq/api-types';
import type { AgentInteractionResolved } from '@ardoq/api';
import { actionCreator } from '@ardoq/rxbeach';
import type { AiThinkingStep } from '@ardoq/ai';
import type {
  ChatMessageId,
  ChatMessageToolCallEvent,
  DecoratedSendMessageRequest,
  Feedback,
  InfoContent,
  UserFacingInteractionResult,
} from './types';
import type { FeedbackCardProps } from './types';
import type {
  AssistantDisplayMode,
  AssistantDisplayModifier,
} from './assistantStateOperations';
import type { DismissedBanner, NewAssistantBanner } from './assistantTypes';
import type { EphemeralNotificationKeys } from 'ephemeralNotification/types';
import type { AssistantTrackingEvents } from './trackingEvents';
import type { RenderStore } from './renderMetadata';
import type { ChartInput } from './components/charts/chartFences';

export const DOMAIN = '[assistant]';

export const openChatWindow = actionCreator<{
  displayMode?: AssistantDisplayMode;
} | void>(`${DOMAIN} openChatWindow`);

export const closeChatWindow = actionCreator(`${DOMAIN} closeChatWindow`);

export const loadDashboardColorPalette = actionCreator(
  `${DOMAIN} loadDashboardColorPalette`
);

export const minimizeChatWindow = actionCreator<{
  silenceEvent?: boolean;
}>(`${DOMAIN} minimizeChatWindow`);

/** Hides the docked assistant when another side panel opens, keeping its conversation and preferences. */
export const hideDockedChatWindow = actionCreator(
  `${DOMAIN} hideDockedChatWindow`
);

export const setAssistantDisplayMode = actionCreator<AssistantDisplayMode>(
  `${DOMAIN} setAssistantDisplayMode`
);

export const setPanelWidth = actionCreator<number>(`${DOMAIN} setPanelWidth`);

export const sendMessageWithHistory =
  actionCreator<DecoratedSendMessageRequest>(
    `${DOMAIN} sendMessageWithHistory`
  );

export const openFullscreenChatAndSend =
  actionCreator<DecoratedSendMessageRequest>(
    `${DOMAIN} openFullscreenChatAndSend`
  );

export const openChatAndPopulate = actionCreator<{ content: string }>(
  `${DOMAIN} openChatAndPopulate`
);

export const sendMessageFailed = actionCreator<string>(
  `${DOMAIN} sendMessageFailed`
);
export const messageCancellationRequested = actionCreator(
  `${DOMAIN} message cancellation requested`
);
export const messageCancellationFailed = actionCreator(
  `${DOMAIN} message cancellation failed`
);
export const durableMessageCancellationAccepted = actionCreator(
  `${DOMAIN} durable message cancellation accepted`
);

/** A durable interaction arrived for the active exchange and is now pending frontend action. */
export const interactionRequested = actionCreator<string>(
  `${DOMAIN} interaction requested`
);
/** The result was accepted; the paused run is resuming on the same exchange watch. */
export const interactionResolved = actionCreator<AgentInteractionResolved>(
  `${DOMAIN} interaction resolved`
);
/** Submitting the result failed; the interaction is retryable from the retained state. */
export const interactionSubmissionFailed = actionCreator<string>(
  `${DOMAIN} interaction submission failed`
);
export type InteractionAnsweredPayload = {
  interactionId: string;
  exchangeId: string;
  result: UserFacingInteractionResult;
};

export const interactionAnswered = actionCreator<InteractionAnsweredPayload>(
  `${DOMAIN} interaction answered`
);

export const dequeueMessages = actionCreator(`${DOMAIN} dequeueMessages`);

export const setInput = actionCreator<string>(`${DOMAIN} setInput`);

export const setEditingPlanMessageId = actionCreator<ChatMessageId | null>(
  `${DOMAIN} setEditingPlanMessageId`
);

export const appendInput = actionCreator<string>(`${DOMAIN} appendInput`);

export const setAssistantConfig = actionCreator<AssistantConfig>(
  `${DOMAIN} setAssistantConfig`
);

export const initializeNewChat = actionCreator(`${DOMAIN} initializeNewChat`);

export const submitFeedback = actionCreator<{
  feedback: Feedback;
  agent?: AgentType;
}>(`${DOMAIN} submitFeedback`);

export const saveSubmittedFeedback = actionCreator<{
  messageId: ChatMessageId;
  feedback: Feedback;
}>(`${DOMAIN} saveSubmittedFeedback`);

export const setAutoScrollInLastMessage = actionCreator<boolean>(
  `${DOMAIN} setAutoScrollInLastMessage`
);

export const setCurrentScrollTop = actionCreator<number>(
  `${DOMAIN} setCurrentScrollTop`
);

export const chatTimedOut = actionCreator(`${DOMAIN} chatTimedOut`);

export const badFeedbackComment = actionCreator<{
  messageId: ChatMessageId;
  feedback: string;
  agent?: AgentType;
}>(`${DOMAIN} badFeedbackComment`);

export type SetFeedbackInputValuePayload = {
  messageId: ChatMessageId;
  value: string;
};

export const setFeedbackInputValue =
  actionCreator<SetFeedbackInputValuePayload>(
    `${DOMAIN} setFeedbackInputValue`
  );

export const showAdditionalFeedbackInput = actionCreator<ChatMessageId>(
  `${DOMAIN} showAdditionalFeedbackInput`
);

export const showFeedbackForm = actionCreator<ChatMessageId>(
  `${DOMAIN} showFeedbackForm`
);

export const hideFeedbackForm = actionCreator<ChatMessageId>(
  `${DOMAIN} hideFeedbackForm`
);

export const showFeedbackFormSuccessMessage = actionCreator<ChatMessageId>(
  `${DOMAIN} showFeedbackFormSuccessMessage`
);

export const dismissSendMessageError = actionCreator(
  `${DOMAIN} dismissSendMessageError`
);

export const showChatHistory = actionCreator(`${DOMAIN} showChatHistory`);
export const hideChatHistory = actionCreator(`${DOMAIN} hideChatHistory`);

export const openSnakeGame = actionCreator(`${DOMAIN} openSnakeGame`);
export const closeSnakeGame = actionCreator(`${DOMAIN} closeSnakeGame`);

export const historicChatTogglePin = actionCreator<HistoricChat>(
  `${DOMAIN} historicChatTogglePin`
);

export type HistoricChatRenamedPayload = {
  chat: HistoricChat;
  title: string;
};

export const historicChatRenamed = actionCreator<HistoricChatRenamedPayload>(
  `${DOMAIN} historicChatRenamed`
);

export const historicChatRenameFailed = actionCreator<HistoricChat>(
  `${DOMAIN} historicChatRenameFailed`
);

export const deleteHistoricChat = actionCreator<string>(
  `${DOMAIN} deleteHistoricChat`
);

export const setHistorySearchText = actionCreator<string>(
  `${DOMAIN} setHistorySearchText`
);

export const setActiveChatSessionId = actionCreator<string>(
  `${DOMAIN} setActiveChatSessionId`
);

export const setCurrentActiveRequest = actionCreator<string>(
  `${DOMAIN} setCurrentActiveRequest`
);

export const openHistoricChat = actionCreator<HistoricChat>(
  `${DOMAIN} openHistoricChat`
);

export const historicChatLoaded = actionCreator<LoadedChat>(
  `${DOMAIN} historicChatLoaded`
);

export const updateHistoricChat = actionCreator<HistoricChat>(
  `${DOMAIN} updateHistoricChat`
);

export const historyLoaded = actionCreator<HistoricChat[]>(
  `${DOMAIN} historyLoaded`
);

export const chatSessionRunStarted = actionCreator<string>(
  `${DOMAIN} chatSessionRunStarted`
);

export const chatSessionRunReconciled = actionCreator<string>(
  `${DOMAIN} chatSessionRunReconciled`
);

export const chatSessionNotificationRead = actionCreator<{
  chatSessionId: string;
}>(`${DOMAIN} chatSessionNotificationRead`);

export const markChatSessionNotificationRead = actionCreator<{
  chatSessionId: string;
}>(`${DOMAIN} markChatSessionNotificationRead`);

export const loadHistory = actionCreator(`${DOMAIN} loadHistory`);

export const historyError = actionCreator(`${DOMAIN} historyError`);

export type AgentRunRestoration = {
  exchangeId: string;
  message?: string;
  toolCalls: ChatMessageToolCallEvent[];
  renderMetadata: RenderStore;
  planningStep: AiThinkingStep;
};

export const agentRunRestored = actionCreator<AgentRunRestoration>(
  `${DOMAIN} agentRunRestored`
);

export const agentRunReconciliationRequired = actionCreator<string>(
  `${DOMAIN} agentRunReconciliationRequired`
);

export const agentRunResumeFailed = actionCreator<{
  exchangeId: string;
}>(`${DOMAIN} agentRunResumeFailed`);

export const lastOpenedSessionRestoreStarted = actionCreator(
  `${DOMAIN} lastOpenedSessionRestoreStarted`
);

export const lastOpenedSessionRestoreFinished = actionCreator(
  `${DOMAIN} lastOpenedSessionRestoreFinished`
);

export const toggleReasoningVisible = actionCreator<ChatMessageId>(
  `${DOMAIN} toggleReasoningVisible`
);

export const appendThinkingStep = actionCreator<{
  clientRequestId: string;
  step: AiThinkingStep;
}>(`${DOMAIN} appendThinkingStep`);

export const scrollToBottom = actionCreator(`${DOMAIN} scrollToBottom`);

export const setShowScrollDownButton = actionCreator<boolean>(
  `${DOMAIN} setShowScrollDownButton`
);

export const setDisplayModifier = actionCreator<AssistantDisplayModifier>(
  `${DOMAIN} setDisplayModifier`
);

export const triggerDisplayModeHint = actionCreator(
  `${DOMAIN} triggerDisplayModeHint`
);

export const dismissDisplayModeHint = actionCreator(
  `${DOMAIN} dismissDisplayModeHint`
);

export const setActiveOnboardingHintKey =
  actionCreator<EphemeralNotificationKeys>(
    `${DOMAIN} setActiveOnboardingHintKey`
  );

export const appendUserFriendlyChatError = actionCreator<{
  content: string;
  errorCode: string;
}>(`${DOMAIN} appendUserFriendlyChatError`);

export const genericChatErrorReceived = actionCreator<{ message: string }>(
  `${DOMAIN} genericChatErrorReceived`
);

export const appendInfoMessage = actionCreator<InfoContent>(
  `${DOMAIN} appendInfoMessage`
);

export const addFeedbackCardToChat = actionCreator<FeedbackCardProps>(
  `${DOMAIN} addFeedbackCardToChat`
);

export type FeedbackCardSubmittedPayload = FeedbackCardProps & {
  feedback: string;
};

export const feedbackCardSubmitted =
  actionCreator<FeedbackCardSubmittedPayload>(
    `${DOMAIN} feedbackCardSubmitted`
  );

export const dismissFeedbackCard = actionCreator<ChatMessageId>(
  `${DOMAIN} dismissFeedbackCard`
);

export const exportChatSession = actionCreator<string>(
  `${DOMAIN} exportChatSession`
);

export const requestAssistantInputFocus = actionCreator<boolean>(
  `${DOMAIN} requestAssistantInputFocus`
);

export const focusAssistantFeature = actionCreator<{
  text: string;
  onboardingHintKey: EphemeralNotificationKeys;
}>(`${DOMAIN} focusAssistantFeature`);

export const addBanner = actionCreator<NewAssistantBanner>(
  `${DOMAIN} addBanner`
);

export const dismissBanner = actionCreator<DismissedBanner>(
  `${DOMAIN} dismissBanner`
);

export type AssistantEventTrackedPayload = {
  eventName: AssistantTrackingEvents;
  metadata: object;
};

export const assistantEventTracked =
  actionCreator<AssistantEventTrackedPayload>(
    `${DOMAIN} assistantEventTracked`
  );

export type ChartDrawOutcomePayload = {
  chartType: ChartInput['chart_type'];
  fenceValue: string;
  outcome: 'drawn' | 'failed_to_draw';
};

export const chartDrawOutcome = actionCreator<ChartDrawOutcomePayload>(
  `${DOMAIN} chartDrawOutcome`
);
